import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Crown, MousePointerClick, Hourglass, Award, Trophy } from "lucide-react";
import { InviteFriendsButton } from "@/components/ShareInvite";

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="card-surface p-4">
      <div className="flex items-center gap-2 text-zinc-500 text-[10px] uppercase tracking-widest mb-1">
        <Icon size={12} /> {label}
      </div>
      <div className="font-display text-2xl">{value}</div>
    </div>
  );
}

export default function PandaPackPage() {
  const [me, setMe] = useState(null);
  const [leaders, setLeaders] = useState([]);

  useEffect(() => {
    api.get("/referrals/me").then(({ data }) => setMe(data)).catch(() => {});
    api.get("/referrals/leaderboard").then(({ data }) => setLeaders(data.leaders)).catch(() => {});
  }, []);

  return (
    <div data-testid="panda-pack-page" className="max-w-4xl mx-auto px-4 lg:px-8 py-6">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl">🐼 Build the Panda Pack</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Invite friends. Earn Pack badges and featured placement — the more pandas, the wilder the vault.
          </p>
        </div>
        <InviteFriendsButton testid="pack-invite-friends-btn" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <Stat icon={MousePointerClick} label="Link Clicks" value={me?.clicks ?? "—"} testid="pack-stat-clicks" />
        <Stat icon={Hourglass} label="Pending" value={me?.pending ?? "—"} testid="pack-stat-pending" />
        <Stat icon={Award} label="Qualified" value={me?.qualified ?? "—"} testid="pack-stat-qualified" />
        <Stat icon={Trophy} label="Leaderboard" value={me?.leaderboard_position ? `#${me.leaderboard_position}` : "—"} testid="pack-stat-rank" />
      </div>

      <div className="card-surface p-5 mb-6" data-testid="pack-badge-card">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Your Pack Badge</div>
        <div className="flex items-center gap-3">
          <div className="font-display text-xl text-amber-300">{me?.badge || "No badge yet"}</div>
          {me?.featured && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-rose-600/20 text-rose-300 px-2 py-1">
              <Crown size={11} /> Featured
            </span>
          )}
        </div>
        {me?.next_badge && (
          <p className="text-xs text-zinc-500 mt-1.5">
            {me.next_badge[0] - (me.qualified || 0)} more qualified referral{me.next_badge[0] - (me.qualified || 0) === 1 ? "" : "s"} to unlock <b className="text-zinc-300">{me.next_badge[1]}</b>.
          </p>
        )}
        <p className="text-[10px] text-zinc-600 mt-2">
          A referral qualifies after your friend verifies their email and completes their profile. Rewards are badges and featured placement only.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="card-surface p-5" data-testid="pack-referred-list">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Your invites</div>
          {(me?.referred?.length ?? 0) === 0 && <p className="text-xs text-zinc-600">No invites yet — share your link!</p>}
          <div className="space-y-2.5">
            {me?.referred?.map((r, i) => (
              <div key={r.handle || `anon-${i}`} className="flex items-center gap-3">
                <Avatar className="w-8 h-8 border border-white/10">
                  <AvatarImage src={r.avatar_url} />
                  <AvatarFallback className="bg-zinc-800 text-xs">🐼</AvatarFallback>
                </Avatar>
                <div className="flex-1 text-sm truncate">{r.handle ? `${r.display_name}` : "A new panda"}</div>
                <span className={`text-[9px] uppercase tracking-widest px-2 py-0.5 ${r.status === "qualified" ? "bg-emerald-500/15 text-emerald-300" : "bg-zinc-800 text-zinc-400"}`}>
                  {r.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="card-surface p-5" data-testid="pack-leaderboard">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Pack Leaderboard</div>
          {leaders.length === 0 && <p className="text-xs text-zinc-600">Be the first on the board.</p>}
          <div className="space-y-2.5">
            {leaders.map((l, i) => (
              <div key={l.handle} className="flex items-center gap-3">
                <span className="w-5 text-xs text-zinc-500">#{i + 1}</span>
                <Avatar className="w-8 h-8 border border-white/10">
                  <AvatarImage src={l.avatar_url} />
                  <AvatarFallback className="bg-zinc-800 text-xs">{l.display_name?.[0]}</AvatarFallback>
                </Avatar>
                <div className="flex-1 text-sm truncate">
                  {l.display_name}
                  {l.referral_featured && <Crown size={11} className="inline ml-1.5 text-amber-400" />}
                </div>
                <span className="text-xs text-zinc-400">{l.referral_qualified_count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
